import { useContext, useState, useEffect } from "react";
import Modal from "../../ui-components/Modal";
import useCountryAxiosInstance from "../../hooks/useCountryAxiosInstance";
import Button from "../../ui-components/Button";
import sharedApiRequest from "../../services/apiRequest";
import { NotificationsContext } from "../../ui-components/Notifications";
import { useIntl, FormattedMessage } from "react-intl";
import Input from "../../ui-components/Input";
import CountryContext from "../../CountryContext";
import { validateEmail } from "@utils/validate-email";

function WorksiteShareFromReferenceModal({ closeModal, worksite_id, setLinkedUsers }) {
  const intl = useIntl();
  const apiRequest = useCountryAxiosInstance();
  const { push } = useContext(NotificationsContext);
  const { country } = useContext(CountryContext);

  const [emailExternalUser, setEmailExternalUser] = useState();
  const [externalUserStatus, setExternalUserStatus] = useState();
  const [canShare, setCanShare] = useState(true);
  const [externalUser, setExternalUser] = useState(undefined);

  const [checking, setChecking] = useState(false);
  const [sharing, setSharing] = useState(false);

  const shareWorksite = async () => {
    setSharing(true);
    try {
      let sharingUser;

      if (externalUser) {
        sharingUser = externalUser;
      } else {
        try {
          const data = {
            email: emailExternalUser,
            country: country.code,
            worksite_id: worksite_id
          };
          await sharedApiRequest.post(`/users/invitate`, data);
          closeModal();
          push({ title: intl.formatMessage({ id: "ok_shared" }), type: "success" });
          return
        } catch (error) {
          console.log("error");
          push({
            title: intl.formatMessage({ id: "server_error" }),
            type: "error",
          });
          return;
        }
      }

      const postData = {
        worksite_id: worksite_id,
        to_user_id: sharingUser["id"],
        to_user_name: sharingUser["firstname"] + " " + sharingUser["lastname"],
        hierarchy: sharingUser["role_hierarchy"],
        email: sharingUser["email"],
      }
      const res = await Promise.all([apiRequest.post('/share', postData)]);
      closeModal();
      if (res[0]['message'] === 'already_shared') {
        push({ title: intl.formatMessage({ id: "already_shared" }), type: "success" });
      } else {
        push({title: intl.formatMessage({id: "ok_shared"}), type: "success"});
        setLinkedUsers(undefined)
      }
    } catch (error) {
      console.log("error", error);
      push({
        title: intl.formatMessage({ id: "server_error" }),
        type: "error",
      });
    } finally {
      setSharing(false);
    }
  };

  const checkExternalUser = async () => {
    setChecking(true);
    sharedApiRequest
      .get(`/users/user?email=${emailExternalUser}&country=${country.code}`)
      .then((res) => {
        if (res === "not_registered") {
          setExternalUserStatus("not_registered");
        } else if (res === "is_internal") {
          setExternalUserStatus("is_internal");
        } else {
          setExternalUser(res);
          setExternalUserStatus("registered");
        }
      })
      .catch(() => {
        push({
          title: intl.formatMessage({ id: "server_error" }),
          type: "error",
        });
      })
      .finally(() => setChecking(false));
  };

  useEffect(() => {
    setCanShare(
      emailExternalUser &&
      externalUserStatus &&
      externalUserStatus !== "is_internal"
    );
  }, [emailExternalUser, externalUserStatus]);


  return (
    <Modal onExit={closeModal}>
      <div className="flex w-full flex-col h-full items-center justify-center">
        <label className="block text-lg font-bold text-black-500">
          <FormattedMessage id="share_with?" />
        </label>
        <>
          <label className="block text-m text-black-500 mt-4 mb-2">
            <FormattedMessage id="insert_external_user_email" />
          </label>
          <div className="w-full">
            <Input
              value={emailExternalUser}
              onChange={(e) => {
                setExternalUser(undefined)
                setEmailExternalUser(e.target.value);
              }}
              placeholder="email"
              type="email"
              errorMessage={intl.formatMessage({ id: "enter_email" })}
              required
            />
          </div>
          {validateEmail(emailExternalUser) && (
            <Button
              variant="contained"
              color="primary"
              onClick={checkExternalUser}
              submitting={checking}
            >
              <FormattedMessage id="validate" />
            </Button>
          )}
          {externalUserStatus === "registered" && (
            <label className="block text-xs text-black-500 m-4">
              <FormattedMessage id="info_sharing_registered" />
            </label>
          )}
          {externalUserStatus === "not_registered" && (
            <div>
              <label className="block text-xs text-black-500 m-4">
                <FormattedMessage id="info_sharing_not_registered" />
              </label>
              <label className="block text-xs font-bold text-black-500 m-4">
                <FormattedMessage id="info_sharing_wait_registration" />
              </label>
            </div>
          )}
          {externalUserStatus === "is_internal" && (
            <label className="block text-xs text-black-500 m-4">
              <FormattedMessage id="info_sharing_is_internal" />
            </label>
          )}
        </>

        <div className="w-full mt-4 flex justify-center">
          {canShare ? (
            <Button
              variant="contained"
              color="primary"
              onClick={shareWorksite}
              submitting={sharing}
            >
              {externalUserStatus === "not_registered" ? (
                <FormattedMessage id="invite_and_share" />
              ) : (
                <FormattedMessage id="share" />
              )}
            </Button>
          ) : null
          }
        </div>
      </div>
    </Modal>
  );
}

export default WorksiteShareFromReferenceModal;
